import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { ShieldOff, Search, Loader2, Clock, ClipboardList, Wallet } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import api from '../../services/api';

const COLS = [
  { field: 'exclude_clock', label: 'Clock-in/out', Icon: Clock,
    hint: 'Hidden from the kiosk, Staff PINs and Live board — cannot clock in/out.' },
  { field: 'exclude_attendance', label: 'Attendance', Icon: ClipboardList,
    hint: 'Hidden from attendance matrix, reports, flagged punches and HR digests.' },
  { field: 'exclude_leave_salary', label: 'Leave Salary', Icon: Wallet,
    hint: 'Excluded from the LOP / leave-salary tracking report.' },
];

const ExclusionsTab = ({ isSuperAdmin }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [savingKey, setSavingKey] = useState(null); // `${id}:${field}`

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/employees?active_only=true');
      const list = (res.data || []).sort((a, b) => (a.employee_code || '').localeCompare(b.employee_code || ''));
      setRows(list);
    } catch {
      toast({ title: 'Error', description: 'Failed to load staff', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(e =>
      (e.name || '').toLowerCase().includes(q)
      || (e.employee_code || '').toLowerCase().includes(q)
      || (e.department || '').toLowerCase().includes(q));
  }, [rows, search]);

  const counts = useMemo(() => {
    const c = { exclude_clock: 0, exclude_attendance: 0, exclude_leave_salary: 0 };
    rows.forEach(e => COLS.forEach(col => { if (e[col.field]) c[col.field]++; }));
    return c;
  }, [rows]);

  const toggle = async (emp, field, value) => {
    setSavingKey(`${emp.id}:${field}`);
    // optimistic
    setRows(prev => prev.map(r => r.id === emp.id ? { ...r, [field]: value } : r));
    try {
      await api.put(`/api/employees/${emp.id}`, { [field]: value });
      toast({ title: value ? 'Excluded' : 'Included', description: `${emp.name} — ${COLS.find(c => c.field === field).label}` });
    } catch (e) {
      // revert
      setRows(prev => prev.map(r => r.id === emp.id ? { ...r, [field]: !value } : r));
      toast({ title: 'Update failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setSavingKey(null);
    }
  };

  if (!isSuperAdmin) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-gray-500">
          <ShieldOff className="h-10 w-10 mx-auto mb-3 text-gray-300" />
          Exclusions are managed by Super Admins only.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="exclusions-tab">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldOff className="h-5 w-5 text-amber-600" /> Staff Exclusions
        </CardTitle>
        <CardDescription>
          Freeze active staff (e.g. management / PROs) from being tracked by Clock-in, Attendance, or Leave Salary —
          all in one place. Toggles save instantly.
        </CardDescription>
        <div className="flex flex-wrap items-center gap-2 pt-2">
          {COLS.map(c => (
            <Badge key={c.field} variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 gap-1">
              <c.Icon className="h-3 w-3" /> {c.label}: {counts[c.field]}
            </Badge>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        <div className="relative mb-3 max-w-sm">
          <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-gray-400" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search name, code or department…"
            className="pl-8"
            data-testid="exclusions-search"
          />
        </div>
        <div className="border rounded-lg overflow-x-auto">
          {loading ? (
            <div className="py-10 text-center text-gray-400 text-sm"><Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading staff…</div>
          ) : filtered.length === 0 ? (
            <div className="py-10 text-center text-gray-400 text-sm" data-testid="exclusions-empty">No matching staff.</div>
          ) : (
            <table className="w-full text-sm min-w-[680px]">
              <thead className="bg-gray-50 text-[11px] uppercase text-gray-500">
                <tr>
                  <th className="text-left p-3">Employee</th>
                  <th className="text-left p-3">Role</th>
                  {COLS.map(c => (
                    <th key={c.field} className="text-center p-3" title={c.hint}>
                      <span className="inline-flex items-center gap-1"><c.Icon className="h-3.5 w-3.5" />{c.label}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(emp => (
                  <tr key={emp.id} className="border-t hover:bg-gray-50/50" data-testid={`exclusion-row-${emp.employee_code}`}>
                    <td className="p-3">
                      <span className="font-medium text-gray-900">{emp.name}</span>
                      <span className="text-xs text-gray-400 ml-1.5 font-mono">{emp.employee_code}</span>
                      {emp.department && <span className="block text-[11px] text-gray-400">{emp.department}</span>}
                    </td>
                    <td className="p-3">
                      <Badge variant="outline" className="text-[10px]">{emp.employee_role || 'Staff'}</Badge>
                    </td>
                    {COLS.map(c => (
                      <td key={c.field} className="p-3 text-center">
                        <Switch
                          checked={!!emp[c.field]}
                          disabled={savingKey === `${emp.id}:${c.field}`}
                          onCheckedChange={(v) => toggle(emp, c.field, v)}
                          data-testid={`exclusion-${c.field}-${emp.employee_code}`}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ExclusionsTab;
